import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSCheckbox } from '../../../shared/components/form-controls';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchAddressAutoComplete from '../../../shared/components/modules/SearchAddressAutoComplete';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import personAddressLinkSchema from '../../schema/personAddressLinkSchema';
import AddAddressDialog from './AddAddressDialog';
import {
  createPersonAddressLink,
  updatePersonAddressLink,
  deletePersonAddressLink,
} from '../../services/userService';

const PersonAddressLinkDialog = ({ open, onClose, personId, addressLink = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const methods = useForm({
    resolver: CreateFormResolver(personAddressLinkSchema),
    mode: 'onSubmit',
    defaultValues: {
      ptsAddressID: addressLink?.ptsAddressID ?? null,
      IsPrimary: Boolean(addressLink?.IsPrimary),
    },
  });

  const { isDirty } = useFormState({ control: methods.control });

  const [addAddressDialogOpen, setAddAddressDialogOpen] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const isEditMode = Boolean(addressLink?.ptsAddressID);

  const ptsAddressID = useWatch({ control: methods.control, name: 'ptsAddressID' });

  const handleAddressCreated = useCallback(
    (address) => {
      methods.setValue('ptsAddressID', address?.ptsAddressID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddAddressDialogOpen(false);
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsPersonID: personId,
        ptsAddressID: data.ptsAddressID,
        IsPrimary: data.IsPrimary || false,
        UpdatedBy: currentUser?.Username,
      };

      if (!isEditMode) payload.CreatedBy = currentUser?.Username;

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode
            ? updatePersonAddressLink(
                { ptsPersonID: personId, ptsAddressID: data.ptsAddressID },
                payload
              )
            : createPersonAddressLink(payload),
        onSuccess: (savedLink) => {
          dispatch(
            notify(
              isEditMode ? 'Address updated successfully' : 'Address created successfully',
              'success'
            )
          );
          onSaved?.(savedLink);
        },
      });
    },
    [personId, currentUser, isEditMode, dispatch, onSaved]
  );

  const handleSave = useCallback(() => methods.handleSubmit(onSubmit)(), [methods, onSubmit]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this address. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  const handleRemove = useCallback(async () => {
    await executeApiAction({
      setLoading: setDeleting,
      dispatch,
      action: () =>
        deletePersonAddressLink({ ptsPersonID: personId, ptsAddressID: addressLink.ptsAddressID }),
      onSuccess: () => {
        dispatch(notify('Address removed successfully', 'success'));
        onSaved?.(null);
      },
    });
  }, [personId, addressLink, dispatch, onSaved]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Address' : 'Add Address'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting || deleting}
      saveDisabled={submitting || deleting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!addressLink?.Created}
            created={addressLink?.Created}
            createdBy={addressLink?.CreatedBy}
            updated={addressLink?.Updated}
            updatedBy={addressLink?.UpdatedBy}
          />
        )
      }
      customButtons={
        isEditMode && (
          <RMSButton
            label="Remove Address From Person Record"
            variant="outlined"
            color="secondary"
            onClick={handleRemove}
            disabled={submitting || deleting}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SearchAddressAutoComplete
              name="ptsAddressID"
              label={isEditMode ? 'Address' : 'Search Address'}
              defaultAddressId={ptsAddressID}
              disabled={isEditMode}
              showAddIcon={!isEditMode}
              showDetailsIcon={false}
              onAddIconClick={() => setAddAddressDialogOpen(true)}
            />
          </Grid>
          <Grid item xs={12}>
            <RMSCheckbox name="IsPrimary" label="Is Primary" />
          </Grid>
        </Grid>
      </FormProvider>

      {addAddressDialogOpen && (
        <AddAddressDialog
          open={addAddressDialogOpen}
          onClose={() => setAddAddressDialogOpen(false)}
          onAddressCreated={handleAddressCreated}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonAddressLinkDialog;
